import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { ButtonLoader } from "Components";
import { newAuthService } from "Service/Axios";
import { saveUserProfileUrl } from "Constant/api";
import { useLanguageList } from "Constant/apiHooks";
import { VIEW_PROFILE_SECTIONS } from "Constant/constants";
import { Validator } from "Utils/validator";

const LanguageProficiencyOptions = [
  "Basic",
  "Conversational",
  "Intermediate",
  "Advance",
  "Native/Bilingual",
];

export const LanguageModal = ({ show, languageData, toggle, fetchData }) => {
  const { t } = useTranslation();

  const { languageList: LanguageOptions, loading: languageListLoading } =
    useLanguageList();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    gid: "",
    languageName: "",
    languageGid: "",
    proficiency: "",
  });
  const [errMsg, setErrMsg] = useState({
    languageName: "",
    proficiency: "",
  });

  const { gid, languageName, languageGid, proficiency } = formData;

  useEffect(() => {
    if (languageData) {
      setFormData({
        gid: languageData.gid || "",
        languageName: languageData.name || "",
        languageGid: languageData.languageGid || "",
        proficiency: languageData.proficiency || "",
      });
    }
  }, [languageData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "languageName") {
      const selectedLang = LanguageOptions.find((l) => l.name === value) || {
        gid: "",
        name: "",
      };
      setFormData((prev) => ({
        ...prev,
        languageName: selectedLang.name,
        languageGid: selectedLang.gid,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    if (!Validator.text(value)) {
      setErrMsg((prev) => ({
        ...prev,
        [name]: t(`common.messages.error.viewProfile.language.${name}.blank`),
      }));
    } else {
      setErrMsg((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const getValidation = () => {
    return new Promise((resolve) => {
      let isValid = true;
      const errors = {};

      if (!Validator.text(languageName)) {
        errors.languageName = t(
          "common.messages.error.viewProfile.language.languageName.blank"
        );
        isValid = false;
      }

      if (!Validator.text(proficiency)) {
        errors.proficiency = t(
          "common.messages.error.viewProfile.language.proficiency.blank"
        );
        isValid = false;
      }

      setErrMsg((prev) => ({
        ...prev,
        ...errors,
      }));

      resolve(isValid);
    });
  };

  const onSaveOnineProfile = async () => {
    const validationStatus = await getValidation();
    if (validationStatus) {
      try {
        const payload = {
          key: VIEW_PROFILE_SECTIONS.languages,
          [VIEW_PROFILE_SECTIONS.languages]: {
            name: languageName,
            languageGid: languageGid,
            proficiency,
          },
        };

        if (gid.trim()) {
          payload[VIEW_PROFILE_SECTIONS.languages].gid = gid;
        }

        setLoading(true);
        const response = await newAuthService(
          saveUserProfileUrl,
          "POST",
          payload
        );
        if (response.status) {
          toast.success(
            t("common.messages.notification.success.profileUpdated")
          );
          fetchData();
          toggle();
        } else {
          toast.error(response.message);
        }
      } catch (error) {
        toast.error(
          t("common.messages.notification.failed.somethingWentWrong")
        );
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Modal show={show} onHide={toggle} className="highlightsModal">
      <Modal.Header closeButton>
        <Modal.Title className="text-md font-medium">
          {t(`auth.viewProfile.${gid ? "editLanguage" : "addLanguage"}`)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row">
          <div className="col-md-12 col-sm-6 col-12">
            <div className="mb-3">
              <label htmlFor="languageName" className="form-label">
                {t("common.label.language")}{" "}
                <span className="text-danger">*</span>
              </label>
              <select
                className="form-select me-2"
                name="languageName"
                id="languageName"
                value={languageName}
                onChange={handleChange}
              >
                <option value="">{t("common.placeHolder.language")}</option>
                {LanguageOptions.map((lang, idx) => (
                  <option key={`${lang.gid}-${idx}`} value={lang.name}>
                    {lang.name}
                  </option>
                ))}
              </select>
              {errMsg.languageName && (
                <p className="error">{errMsg.languageName}</p>
              )}
            </div>
          </div>
          <div className="col-md-12 col-sm-6 col-12">
            <div className="mb-3">
              <label htmlFor="proficiency" className="form-label">
                {t("common.label.proficiency")}{" "}
                <span className="text-danger">*</span>
              </label>
              <select
                className="form-select me-2"
                value={proficiency}
                name="proficiency"
                id="proficiency"
                onChange={handleChange}
              >
                <option value="">{t("common.placeHolder.proficiency")}</option>
                {LanguageProficiencyOptions.map((option) => (
                  <option value={option} key={option}>
                    {option}
                  </option>
                ))}
              </select>
              {errMsg.proficiency && (
                <p className="error">{errMsg.proficiency}</p>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={toggle}>
          {t("common.button.cancel")}
        </Button>
        {loading ? (
          <ButtonLoader />
        ) : (
          <Button
            variant="primary"
            onClick={onSaveOnineProfile}
            disabled={languageListLoading}
          >
            {t("common.button.save")}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
